import { useCallback, useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";

import { mutateData } from "@/core/api/api-methods";
import {
  MAX_PAGE_SIZE,
  pageCount,
  type Paged,
} from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import { useTransactionDisputes } from "@/features/wallet/api/use-transaction-disputes";
import type {
  TransactionDisputeDto,
  TransactionDisputeFilter,
} from "@/features/wallet/api/dto";
import type { TransactionDisputeStatus } from "@/features/wallet/api/enums";
import {
  disputesColumns,
  type TransactionDisputeRow,
} from "@/features/wallet/components";

/** Traduit les filtres de colonnes DataTableClar en `TransactionDisputeFilter` (Postman `POST /TransactionDisputes/all`). */
const toTransactionDisputeFilter = (
  filters: ColumnFiltersState
): TransactionDisputeFilter => {
  const filter: TransactionDisputeFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "status") filter.status = value as TransactionDisputeStatus;
    if (id === "transactionId") filter.transactionId = value;
  }
  return filter;
};

/**
 * File de travail des contestations de transfert (`POST
 * /TransactionDisputes/all`, guide v3.1) — filtres `status` (select
 * OPEN/REVERSED/REJECTED) et `transactionId` (texte). Un client n'obtient
 * que ses propres ouvertures ; le back-office voit l'ensemble. Route cible :
 * `/app/wallet/disputes` (câblage router/nav : phase 18.7).
 */
export function DisputesPage() {
  const { t } = useTypedTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toTransactionDisputeFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useTransactionDisputes(params);

  const rows = useMemo(
    () =>
      (data?.items ?? []).filter(
        (d): d is TransactionDisputeRow => Boolean(d.id)
      ),
    [data]
  );

  // Export complet : boucle sur `POST /TransactionDisputes/all` à
  // `MAX_PAGE_SIZE` (200, guide §4.5) jusqu'à épuisement du `total`. Les
  // filtres actifs sont réappliqués via `params.filter`.
  const fetchAllDisputesForExport = useCallback(async (): Promise<
    TransactionDisputeRow[]
  > => {
    const collected: TransactionDisputeDto[] = [];
    let page = 1;
    let total = Infinity;
    while (collected.length < total) {
      const paged = (await mutateData({
        url: walletEndpoints.transactionDisputes.all,
        reqBody: {
          filter: params.filter,
          page,
          pageSize: MAX_PAGE_SIZE,
        } as unknown as Record<string, unknown>,
        method: "POST",
      })) as Paged<TransactionDisputeDto>;
      total = paged.total;
      collected.push(...paged.items);
      if (!paged.items.length) break;
      page += 1;
    }
    return collected.filter(
      (d): d is TransactionDisputeRow => Boolean(d.id)
    );
  }, [params.filter]);

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold text-foreground">
        {t("wallet.disputes.title")}
      </h1>

      <DataTableClar
        data={rows}
        columnsClar={disputesColumns}
        title={t("wallet.disputes.title")}
        exportFileName="transaction-disputes"
        fetchAllForExport={fetchAllDisputesForExport}
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />
    </section>
  );
}
